/**
 * Shared goal progress — gym stays "how close is current to target".
 * Running/time-trial: how much of the start→goal gap you have closed.
 */

import { runningTimeProgressPercent } from '@/lib/specificGoals';

export interface GoalForProgress {
  goal_type?: string | null;
  exercise_name?: string | null;
  target_value: number;
  target_unit?: string | null;
  current_value: number;
  starting_value?: number | null;
  description?: string | null;
  target_date?: string | null;
  created_at?: string | null;
}

export function getGoalProgressPercentage(goal: GoalForProgress): number {
  const type = (goal.goal_type || '').toLowerCase();
  const name = (goal.exercise_name || '').toLowerCase();
  const unit = (goal.target_unit || '').toLowerCase();

  if (type === 'running') {
    return runningTimeProgressPercent({
      targetMinutes: parseFloat(goal.description || '0') || 0,
      currentMinutes: goal.current_value,
      startingMinutes: goal.starting_value,
    });
  }

  const isWeightGoal =
    type === 'weight' ||
    name.includes('body weight') ||
    name.includes('weight loss') ||
    name.includes('weight gain') ||
    name.includes('body fat') ||
    name === 'weight';

  if (isWeightGoal) {
    const current = goal.current_value;
    const target = goal.target_value;
    const starting = goal.starting_value ?? current;

    if (!target || current == null) return 0;
    const isLossGoal = starting > target;
    const isGainGoal = starting < target;

    if (isLossGoal) {
      if (current <= target) return 100;
      if (current >= starting) return 0;
      const totalToLose = starting - target;
      const actualLost = starting - current;
      return Math.min(Math.max((actualLost / totalToLose) * 100, 0), 99);
    }
    if (isGainGoal) {
      if (current >= target) return 100;
      if (current <= starting) return 0;
      const totalToGain = target - starting;
      const actualGained = current - starting;
      return Math.min(Math.max((actualGained / totalToGain) * 100, 0), 99);
    }
    return current === target ? 100 : 0;
  }

  // Endurance time holds (plank) — longer is better, same shape as a lift.
  if (type === 'endurance' && (unit === 'minutes' || unit === 'min' || unit === 'seconds' || unit === 'sec')) {
    const current = goal.current_value;
    const target = goal.target_value;
    const starting = goal.starting_value ?? 0;
    if (!target || current == null) return 0;
    if (current >= target) return 100;
    const start = starting === current && current > 0 ? 0 : starting;
    if (current <= start) return 0;
    const total = target - start;
    return total > 0 ? Math.min(Math.max(((current - start) / total) * 100, 0), 99) : 0;
  }

  // Strength / endurance reps — higher is better
  const current = goal.current_value;
  const target = goal.target_value;
  const starting = goal.starting_value ?? (current > 0 ? 0 : current);

  if (!target || current == null) return 0;
  if (current >= target) return 100;

  const effectiveStarting = starting === current && current > 0 ? 0 : starting;
  if (current <= effectiveStarting) return 0;

  const totalToGain = target - effectiveStarting;
  const actualGained = current - effectiveStarting;
  return totalToGain > 0 ? Math.min(Math.max((actualGained / totalToGain) * 100, 0), 99) : 0;
}
